import { applyPatch, assignOid, cloneDeep, EventSubscriber, } from '@verdant-web/common';
import { Entity, refreshEntity } from './Entity.js';
import { WeakRef } from './FakeWeakRef.js';
export class DocumentFamilyCache extends EventSubscriber {
    constructor({ oid, store, context, }) {
        super();
        this.entities = new Map();
        this.insertUnconfirmedOperations = (operations) => {
            const oidSet = new Set();
            for (const operation of operations) {
                const { oid } = operation;
                oidSet.add(oid);
                const existingOperations = this.unconfirmedOperationsMap.get(oid) || [];
                existingOperations.push(operation);
                this.unconfirmedOperationsMap.set(oid, existingOperations);
            }
            for (const oid of oidSet) {
                const entityRef = this.entities.get(oid);
                const entity = entityRef === null || entityRef === void 0 ? void 0 : entityRef.deref();
                if (entity) {
                    refreshEntity(entity, { isLocal: true });
                    this.emit(`change:${oid}`);
                    this.emit('change:*', oid);
                }
            }
        };
        this.insertOperations = (operations, info) => {
            var _a;
            for (const operation of operations) {
                const { oid } = operation;
                (_a = info.affectedOids) === null || _a === void 0 ? void 0 : _a.add(oid);
                const existingOperations = this.operationsMap.get(oid) || [];
                // insert in order of timestamp
                const index = existingOperations.findIndex((op) => op.timestamp >= operation.timestamp);
                // ensure the operation doesn't already exist in this position
                if (index !== -1) {
                    if (existingOperations[index].timestamp === operation.timestamp) {
                        continue;
                    }
                    existingOperations.splice(index, 0, operation);
                }
                else {
                    existingOperations.push(operation);
                }
                this.operationsMap.set(oid, existingOperations);
                // FIXME: seems inefficient
                const unconfirmedOperations = this.unconfirmedOperationsMap.get(oid);
                if (unconfirmedOperations) {
                    this.unconfirmedOperationsMap.set(oid, unconfirmedOperations.filter((op) => op.timestamp !== operation.timestamp));
                }
            }
        };
        /**
         * Insert new baselines for objects in this family.
         * Automatically drops operations before the new baseline.
         */
        this.insertBaselines = (baselines, { affectedOids, }) => {
            var _a;
            for (const baseline of baselines) {
                const { oid } = baseline;
                affectedOids === null || affectedOids === void 0 ? void 0 : affectedOids.add(oid);
                this.baselinesMap.set(oid, baseline);
                // drop operations before the baseline
                const ops = this.operationsMap.get(oid) || [];
                while (((_a = ops[0]) === null || _a === void 0 ? void 0 : _a.timestamp) < baseline.timestamp) {
                    ops.shift();
                }
            }
        };
        this.addConfirmedData = ({ operations, baselines, reset, isLocal, }) => {
            if (reset) {
                this.operationsMap.clear();
                this.baselinesMap.clear();
            }
            const info = {
                isLocal: isLocal || false,
                affectedOids: new Set(),
            };
            this.insertBaselines(baselines, info);
            // for reset scenario, don't immediately update entities;
            // we will update all of them in one go.
            this.insertOperations(operations, info);
            if (reset) {
                for (const entityRef of this.entities.values()) {
                    const entity = entityRef.deref();
                    if (entity) {
                        refreshEntity(entity, info);
                    }
                }
            }
            else {
                for (const oid of info.affectedOids) {
                    const entityRef = this.entities.get(oid);
                    const entity = entityRef === null || entityRef === void 0 ? void 0 : entityRef.deref();
                    if (entity) {
                        refreshEntity(entity, info);
                        this.emit(`change:${oid}`);
                        this.emit('change:*', oid);
                    }
                }
            }
        };
        this.applyOperations = (view, deleted, operations, after) => {
            for (const operation of operations) {
                if (after && operation.timestamp <= after) {
                    continue;
                }
                if (operation.data.op === 'delete') {
                    deleted = true;
                }
                else {
                    view = applyPatch(view, operation.data);
                    if (operation.data.op === 'initialize') {
                        deleted = false;
                    }
                }
            }
            return { view, deleted };
        };
        this.computeView = (oid) => {
            const confirmed = this.computeConfirmedView(oid);
            const unconfirmedOperations = this.unconfirmedOperationsMap.get(oid) || [];
            let { view, deleted } = this.applyOperations(confirmed.view, confirmed.deleted, unconfirmedOperations);
            if (view) {
                assignOid(view, oid);
            }
            return { view, deleted, lastTimestamp: this.getLastTimestamp(oid) };
        };
        this.computeConfirmedView = (oid) => {
            const baseline = this.baselinesMap.get(oid);
            const operations = this.operationsMap.get(oid) || [];
            let view = cloneDeep((baseline === null || baseline === void 0 ? void 0 : baseline.snapshot) || undefined);
            view = this.applyOperations(view, !view, operations, baseline === null || baseline === void 0 ? void 0 : baseline.timestamp);
            if (view) {
                assignOid(view, oid);
            }
            if (!baseline && !operations.length) {
                this.context.log('debug', `Entity ${oid} accessed with no confirmed data`);
            }
            return view;
        };
        this.getLastTimestamp = (oid) => {
            var _a, _b, _c;
            let operations = this.unconfirmedOperationsMap.get(oid);
            if (!(operations === null || operations === void 0 ? void 0 : operations.length)) {
                operations = this.operationsMap.get(oid) || [];
            }
            let logicalTimestamp = null;
            if (operations.length) {
                logicalTimestamp = (_a = operations[operations.length - 1]) === null || _a === void 0 ? void 0 : _a.timestamp;
            }
            else {
                logicalTimestamp = (_c = (_b = this.baselinesMap.get(oid)) === null || _b === void 0 ? void 0 : _b.timestamp) !== null && _c !== void 0 ? _c : null;
            }
            if (!logicalTimestamp)
                return null;
            return this.storeTools.time.getWallClockTime(logicalTimestamp);
        };
        this.getEntity = (oid, schema, parent) => {
            let entityRef = this.entities.get(oid);
            let entity = entityRef === null || entityRef === void 0 ? void 0 : entityRef.deref();
            if (!entity) {
                entity = new Entity({
                    oid,
                    cache: this,
                    fieldSchema: schema,
                    store: this.storeTools,
                    parent,
                });
                // immediately add to cache and queue a removal if nobody subscribed
                this.entities.set(oid, new WeakRef(entity));
            }
            return entity;
        };
        this.hasOid = (oid) => {
            return this.operationsMap.has(oid) || this.baselinesMap.has(oid);
        };
        this.dispose = () => {
            this.entities.forEach((entity) => { var _a; return (_a = entity.deref()) === null || _a === void 0 ? void 0 : _a.dispose(); });
            this.entities.clear();
        };
        this.reset = (operations, baselines) => {
            const info = { isLocal: false, affectedOids: new Set() };
            this.baselinesMap = new Map(baselines.map((baseline) => [baseline.oid, baseline]));
            this.operationsMap = new Map();
            this.insertOperations(operations, info);
            for (const oid of this.entities.keys()) {
                const entityRef = this.entities.get(oid);
                const entity = entityRef === null || entityRef === void 0 ? void 0 : entityRef.deref();
                if (entity) {
                    refreshEntity(entity, info);
                    this.emit(`change:${oid}`);
                    this.emit('change:*', oid);
                }
            }
        };
        this.oid = oid;
        this.operationsMap = new Map();
        this.unconfirmedOperationsMap = new Map();
        this.baselinesMap = new Map();
        this.storeTools = {
            addLocalOperations: store.addLocalOperations,
            patchCreator: store.meta.patchCreator,
            addFile: store.files.add,
            getFile: store.files.get,
            time: store.meta.time,
        };
        this.context = context;
    }
}
//# sourceMappingURL=DocumentFamiliyCache.js.map